import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getFileType, isImageExtension, isFontExtension } from '@/lib/fileConstants';

export function useProjectFiles(projectId?: string) {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ['project-files', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_files')
        .select('*')
        .eq('project_id', projectId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user && !!projectId,
    refetchInterval: (query) => {
      const files = query.state.data;
      if (!files) return false;
      const hasPending = files.some(f => f.parsing_status === 'pending' || f.parsing_status === 'processing');
      return hasPending ? 3000 : false;
    },
  });
  return query;
}

export function useProjectFileStats(projectId?: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['project-file-stats', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_file_stats')
        .select('*')
        .eq('project_id', projectId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user && !!projectId,
  });
}

export function useUploadProjectFile() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ projectId, file }: { projectId: string; file: File }) => {
      if (!user) throw new Error('Usuário não autenticado');

      const fileId = crypto.randomUUID();
      const sanitizedName = file.name
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `${projectId}/${fileId}/${sanitizedName}`;

      const { error: uploadError } = await supabase.storage
        .from('project-files')
        .upload(storagePath, file);
      if (uploadError) throw uploadError;

      const isImage = isImageExtension(file.name);
      const isFont = isFontExtension(file.name);

      const { data, error: insertError } = await supabase
        .from('project_files')
        .insert({
          id: fileId,
          project_id: projectId,
          file_name: file.name,
          file_type: getFileType(file.name),
          file_size_bytes: file.size,
          mime_type: file.type || 'application/octet-stream',
          storage_path: storagePath,
          uploaded_by: user.id,
          parsing_status: isImage || isFont ? 'ready' : 'pending',
        })
        .select()
        .single();
      if (insertError) throw insertError;

      // For documents, trigger parsing; images and fonts are ready immediately
      if (!isImage && !isFont) {
        supabase.functions.invoke('parse-document', {
          body: { fileId },
        }).then(({ error }) => {
          if (error) console.error('parse-document invoke error:', error);
          queryClient.invalidateQueries({ queryKey: ['project-files', projectId] });
          queryClient.invalidateQueries({ queryKey: ['project-file-stats', projectId] });
        });
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['project-files', data.project_id] });
      queryClient.invalidateQueries({ queryKey: ['project-file-stats', data.project_id] });
    },
  });
}

export function useDeleteProjectFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ fileId, storagePath, projectId }: { fileId: string; storagePath: string; projectId: string }) => {
      const { error: storageError } = await supabase.storage
        .from('project-files')
        .remove([storagePath]);
      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from('project_files')
        .delete()
        .eq('id', fileId);
      if (dbError) throw dbError;

      return { projectId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['project-files', data.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-file-stats', data.projectId] });
    },
  });
}
